import { DetailPageAccessMode } from '@app/detailPageLayout.models';
import { useMutationWithOnSuccessOnError } from '@common/useMutationWithOnSuccessOnError.hooks';
import { envConfigs } from '@config/envConfigs';
import { ApiErrorDisplay } from '@core/ApiErrorDisplay';
import { AxiosErrorResponse } from '@core/error.models';
import { Tooltip, CircularProgressBar, TableRowExtras, useMsal, Loader, ComponentType } from '@gcl/react-core';
import { QueryKey, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { v4 as uuidV4 } from 'uuid';
import { useAppConfigs } from '@config/config.hooks';
import {
  IFormFileAttachmentItem,
  IProjectFileBase,
  IProjectFileProcessResponse,
  TFileDisplay,
} from './projectFile/projectFile.models';
import { useUploadProjectFileAttachmentAsync } from './projectFile/projectFile.queries';
import { IUploadProjectFileAsyncConfigProps, IUploadProjectFileAsyncProps } from './projectFile/projectFile.services';

interface IFileUploadWithDisplayLoadingColumnProps {
  rowExtras: TableRowExtras;
  item: IFormFileAttachmentItem;
  useFetchFilesQueryKey?: QueryKey;
  mode: DetailPageAccessMode;
  type: TFileDisplay;
  setLocalFiles: (value: (old: IFormFileAttachmentItem[]) => IFormFileAttachmentItem[], shouldDirty?: boolean) => void;
  generateUseUploadProjectFileAsync?: (
    configProps: IUploadProjectFileAsyncConfigProps,
  ) => UseMutationResult<IProjectFileProcessResponse, AxiosErrorResponse, any, unknown>;
  onUploadStatusChanged?: (uuid: string, isUploading: boolean) => void;
  index: number;
  setRowUploading: (index: number, isUploading: boolean) => void;
}

export const FileUploadWithDisplayLoadingColumn: (
  props: IFileUploadWithDisplayLoadingColumnProps,
) => React.ReactElement = ({
  rowExtras,
  item,
  useFetchFilesQueryKey,
  mode,
  setLocalFiles,
  generateUseUploadProjectFileAsync = useUploadProjectFileAttachmentAsync,
  onUploadStatusChanged,
  index,
  setRowUploading,
  type,
}) => {
  const { instance } = useMsal();
  const queryClient = useQueryClient();
  const appConfigs = useAppConfigs();

  const [uploadProgress, setUploadProgress] = React.useState<number | undefined>(undefined);
  const [isUploaded, setIsUploaded] = useState(false);
  const uuid = React.useMemo(() => item?.form?.uuid || uuidV4(), []);

  const { rowState, setRowState } = rowExtras;
  const useUploadProjectFileAsync = React.useMemo(() => {
    return () =>
      generateUseUploadProjectFileAsync({
        apiTokenProps: {
          scopes: [envConfigs.apiScope as string],
          instance,
        },
        traceId: appConfigs.configs.traceId,
      });
  }, [generateUseUploadProjectFileAsync]);

  const {
    mutate: mutateUploadProjectFile,
    error: errorUploadProjectFile,
    data: dataUploadProjectFile,
  } = useMutationWithOnSuccessOnError<IProjectFileProcessResponse>(
    useUploadProjectFileAsync,
    (variables, result) => {
      setRowUploading(index, false);

      if (onUploadStatusChanged && typeof onUploadStatusChanged === 'function') onUploadStatusChanged(uuid, false);
      if (mode === 'Update' && useFetchFilesQueryKey) {
        if (type === 'supplementaryFile') {
          const oldData: IProjectFileBase[] =
            (queryClient.getQueryData(useFetchFilesQueryKey) as IProjectFileBase[]) ?? [];
          const newData = [...oldData];
          if (result.item) newData[index] = result.item;
          queryClient.setQueryData(useFetchFilesQueryKey, newData);
        }

        queryClient.invalidateQueries(useFetchFilesQueryKey);
      }
      if (setRowState) setRowState('Idle');
    },
    (variables, error) => {
      if (onUploadStatusChanged && typeof onUploadStatusChanged === 'function') onUploadStatusChanged(uuid, false);
      if (setRowState) setRowState('Idle');
      setLocalFiles((old) => old.filter((oldItem) => oldItem.form?.uuid !== item.form?.uuid));
      setRowUploading(index, false);
    },
    true,
  );

  useEffect(() => {
    if (!item.file || rowState !== 'Idle' || uploadProgress !== undefined || isUploaded) return;
    setIsUploaded(true);
    handleSubmit();
  }, [isUploaded]);

  useEffect(() => {
    if (mode !== 'Create' || !dataUploadProjectFile?.isSuccess) return;
    setLocalFiles((old) => {
      return old.map((oldItem) => {
        if (oldItem.form?.uuid === dataUploadProjectFile?.item?.uuid) {
          const { file, ...rest } = oldItem;
          return {
            ...rest,
            fileInfo: dataUploadProjectFile?.item,
            form: {
              ...dataUploadProjectFile?.item,
              isTemp: true,
              projectId: dataUploadProjectFile?.item?.projectId ?? 0,
              uuid: dataUploadProjectFile?.uuid ?? oldItem.form?.uuid ?? '',
              fileName: dataUploadProjectFile?.item?.fileName ?? oldItem.form?.fileName ?? '',
            },
          };
        }
        return oldItem;
      });
    });
  }, [JSON.stringify(dataUploadProjectFile), mode]);

  const handleSubmit = () => {
    if (!item.file || uploadProgress !== undefined) return;
    setUploadProgress(0);
    if (onUploadStatusChanged && typeof onUploadStatusChanged === 'function') onUploadStatusChanged(uuid, true);
    if (setRowState) {
      setRowState('Loading');
    }
    setRowUploading(index, true);

    const requestData = {
      ...item?.fileInfo,
      ...item.form,
    };

    const updateProjectFileRequest = requestData;

    const uploadRequest: IUploadProjectFileAsyncProps<any> = {
      request: {
        file: item.file,
        ...updateProjectFileRequest,
        isTemp: mode === 'Create',
      },
      onUploadProgress: (progress) => {
        setUploadProgress(progress);
      },
    };

    mutateUploadProjectFile(uploadRequest);
  };

  if (rowState !== 'Loading') return <></>;

  return (
    <>
      <ApiErrorDisplay
        errors={[
          {
            error: errorUploadProjectFile,
            genericError: `Upload ${item.form?.fileName || 'file'} failed`,
          },
        ]}
      />
      {uploadProgress === undefined ? (
        <Loader small type={ComponentType.Primary} />
      ) : (
        <>
          <Tooltip
            overlay={`${item?.form?.fileName || 'File'} uploading`}
            id={`gcl-gems-transmittal-item-table-form-${uuid}-tooltip`}
          >
            <CircularProgressBar
              value={uploadProgress || 0}
              className="gcl-gems-upload-with-display-upload-progress-bar"
            />
          </Tooltip>
        </>
      )}
    </>
  );
};
